import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { teamService } from "@/services/api";
import { roleDefs } from "@/services/mockData";
import { PageHeader } from "@/components/common/PageHeader";
import { SkeletonTable } from "@/components/common/Skeletons";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { relativeTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { UserPlus, MoreHorizontal, Shield, Ban, UserX } from "lucide-react";

const ROLE_STYLES = {
  "Organization Admin": "bg-primary/15 text-primary border-primary/25",
  "Fleet Manager": "bg-sky-400/10 text-sky-700 dark:text-sky-300 border-sky-400/20",
  Operator: "bg-emerald-400/10 text-emerald-700 dark:text-emerald-300 border-emerald-400/20",
  Viewer: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20",
};

export default function Team() {
  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  const { data: members, loading, refetch } = useFetch(() => teamService.list(), []);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("Operator");
  const [inviting, setInviting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const list = members || [];

  const changeRole = async (id, role) => {
    setBusyId(id);
    try {
      await teamService.changeRole(id, role);
      toast.success("Role updated");
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not update role");
    } finally {
      setBusyId(null);
    }
  };

  const toggleDisabled = async (m) => {
    setBusyId(m.id);
    try {
      await teamService.setStatus(m.id, m.status === "disabled" ? "active" : "disabled");
      toast.success(m.status === "disabled" ? "Member enabled" : "Member disabled");
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not update member");
    } finally {
      setBusyId(null);
    }
  };

  const removeMember = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await teamService.remove(removeTarget.id);
      toast.success("Member removed");
      setRemoveTarget(null);
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not remove member");
    } finally {
      setRemoving(false);
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    try {
      const member = await teamService.invite({ email: inviteEmail, role: inviteRole });
      setInviteOpen(false);
      setInviteEmail("");
      refetch();
      // The invite link is always copied too, as a fallback the admin can
      // hand-deliver if the email bounces or SMTP isn't configured.
      navigator.clipboard?.writeText(member.inviteUrl);
      toast.success("Member added", {
        description: member.inviteEmailSent
          ? `Invite email sent to ${inviteEmail}. The link was also copied to your clipboard.`
          : "Could not confirm the invite email sent — the invite link was copied to your clipboard to share manually.",
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not send invite");
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader
        title="Team"
        subtitle="Manage members, roles and permissions across your organization."
        actions={isAdmin && (
          <Button onClick={() => setInviteOpen(true)} data-testid="invite-member"><UserPlus className="mr-1.5 h-4 w-4" /> Invite Member</Button>
        )}
      />

      <div className="mb-6 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {roleDefs.map((r) => (
          <Card key={r.role} className="border-border/70 bg-card/50 p-4">
            <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{r.role}</span></div>
            <p className="mt-2 text-xs text-muted-foreground">{r.description}</p>
            <div className="mt-3 text-[11px] uppercase tracking-wider text-muted-foreground">{r.access}</div>
          </Card>
        ))}
      </div>

      {loading ? <SkeletonTable rows={6} /> : (
        <div className="overflow-hidden rounded-2xl border border-border/70 bg-card/40">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border/60">
                <TableHead>Member</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Last Active</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((m) => (
                <TableRow key={m.id} className="border-border/50" data-testid={`team-row-${m.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9 border border-border/60"><AvatarFallback className="bg-secondary text-xs font-semibold">{m.initials}</AvatarFallback></Avatar>
                      <div><div className="font-medium">{m.name}</div><div className="text-xs text-muted-foreground">{m.email}</div></div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {isAdmin ? (
                      <Select value={m.role} onValueChange={(r) => changeRole(m.id, r)} disabled={busyId === m.id}>
                        <SelectTrigger className={cn("h-8 w-[180px] border text-xs", ROLE_STYLES[m.role])} data-testid={`role-select-${m.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{roleDefs.map((r) => <SelectItem key={r.role} value={r.role}>{r.role}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <span className={cn("inline-flex h-8 w-[180px] items-center rounded-md border px-3 text-xs", ROLE_STYLES[m.role])}>{m.role}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
                      m.status === "active" ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-700 dark:text-emerald-300" :
                      m.status === "invited" ? "border-sky-400/20 bg-sky-400/10 text-sky-700 dark:text-sky-300" : "border-zinc-500/20 bg-zinc-500/10 text-zinc-600 dark:text-zinc-400")}>
                      {m.status === "active" ? "Active" : m.status === "invited" ? "Invited" : "Disabled"}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{m.lastActive ? relativeTime(m.lastActive) : "Pending invite"}</TableCell>
                  <TableCell className="text-right">
                    {isAdmin && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><button className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground" data-testid={`team-actions-${m.id}`} disabled={busyId === m.id}><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => toggleDisabled(m)} className="text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"><Ban className="mr-2 h-4 w-4" /> {m.status === "disabled" ? "Enable" : "Disable"} member</DropdownMenuItem>
                          {m.id !== user?.id && (
                            <DropdownMenuItem onClick={() => setRemoveTarget(m)} className="text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400" data-testid={`remove-member-${m.id}`}><UserX className="mr-2 h-4 w-4" /> Remove member</DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader><DialogTitle>Invite team member</DialogTitle><DialogDescription>Adds them to your organization with the chosen role. Invite emails aren't sent yet — you'll get a link to share with them yourself.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Email address</Label><Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="colleague@company.com" data-testid="invite-email-input" /></div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{roleDefs.map((r) => <SelectItem key={r.role} value={r.role}>{r.role}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button onClick={sendInvite} disabled={inviting || !inviteEmail} data-testid="invite-submit">{inviting ? "Sending…" : "Send invite"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title="Remove team member?"
        description={removeTarget ? `${removeTarget.name} (${removeTarget.email}) will lose access to this organization immediately. This can't be undone.` : ""}
        confirmLabel={removing ? "Removing…" : "Remove member"}
        destructive
        onConfirm={removeMember}
      />
    </div>
  );
}
